import { useState, useRef, useEffect, useCallback } from 'react';
import {
  fetchFiles,
  fetchFilePreview,
  downloadArchive,
} from '../services/generatorBatchApi';

/**
 * Gestisce i risultati di un batch: elenco file, preview e download ZIP.
 * Le richieste vengono annullate allo smontaggio e la preview è cache-ata.
 */
export function useBatchArtifacts(batchId) {
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState(null);

  const [activeFileId, setActiveFileId] = useState(null);
  const [previewCache, setPreviewCache] = useState({});
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const [downloading, setDownloading] = useState(false);

  const filesAbort = useRef(null);
  const previewAbort = useRef(null);

  useEffect(
    () => () => {
      if (filesAbort.current) filesAbort.current.abort();
      if (previewAbort.current) previewAbort.current.abort();
    },
    [],
  );

  // Cambio batch: azzera file, preview e cache (i fileId possono ripetersi tra batch).
  useEffect(() => {
    setFiles([]);
    setFilesError(null);
    setActiveFileId(null);
    setPreviewCache({});
    setPreviewError(null);
  }, [batchId]);

  const loadFiles = useCallback(() => {
    if (!batchId) return;
    if (filesAbort.current) filesAbort.current.abort();
    const controller = new AbortController();
    filesAbort.current = controller;
    setFilesLoading(true);
    setFilesError(null);
    fetchFiles(batchId, controller.signal)
      .then((data) => setFiles(Array.isArray(data?.items) ? data.items : []))
      .catch((err) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        setFilesError('Impossibile elencare i file generati.');
        setFiles([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setFilesLoading(false);
      });
  }, [batchId]);

  const openPreview = useCallback(
    (fileId) => {
      if (!batchId || !fileId) return;
      setActiveFileId(fileId);
      if (previewCache[fileId]) {
        setPreviewError(null);
        return;
      }
      if (previewAbort.current) previewAbort.current.abort();
      const controller = new AbortController();
      previewAbort.current = controller;
      setPreviewLoading(true);
      setPreviewError(null);
      fetchFilePreview(batchId, fileId, controller.signal)
        .then((data) => {
          setPreviewCache((prev) => ({ ...prev, [fileId]: data }));
        })
        .catch((err) => {
          if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
          const status = err?.response?.status;
          setPreviewError(
            status === 415
              ? 'Anteprima non disponibile: file binario.'
              : 'Impossibile caricare l’anteprima del file.',
          );
        })
        .finally(() => {
          if (!controller.signal.aborted) setPreviewLoading(false);
        });
    },
    [batchId, previewCache],
  );

  const closePreview = useCallback(() => {
    if (previewAbort.current) previewAbort.current.abort();
    setActiveFileId(null);
    setPreviewError(null);
  }, []);

  const download = useCallback(() => {
    if (!batchId) return;
    setDownloading(true);
    downloadArchive(batchId).finally(() => setDownloading(false));
  }, [batchId]);

  return {
    files,
    filesLoading,
    filesError,
    loadFiles,
    activeFileId,
    preview: activeFileId ? previewCache[activeFileId] : null,
    previewLoading,
    previewError,
    openPreview,
    closePreview,
    downloading,
    download,
  };
}
